import { useParams, Link } from "react-router-dom";
import { articles, categories } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter(
    (a) => a.category === currentCategory?.id
  );

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "http://dlqlbdnn.cn/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": currentCategory?.name || "分类",
        "item": `http://dlqlbdnn.cn/category/${slug}`
      }
    ]
  };

  const webPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${currentCategory?.name || "分类"} - 洞察日报`,
    "description": currentCategory?.description || "浏览该分类下的所有文章",
    "url": `http://dlqlbdnn.cn/category/${slug}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "洞察日报",
      "url": "http://dlqlbdnn.cn/"
    }
  };

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }}
      />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="px-4 md:px-8 mb-8">
          <nav className="text-sm text-foreground-500 mb-6">
            <Link to="/" className="hover:text-foreground-700 transition-colors">
              首页
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-950">
              {currentCategory?.name || "分类"}
            </span>
          </nav>

          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
            {currentCategory?.name || "分类"}
          </h1>
          <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
            {currentCategory?.description || "浏览该分类下的所有文章"}
          </p>
        </div>

        <div className="px-4 md:px-8 mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  cat.slug === slug
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="px-4 md:px-8 pb-12 md:pb-20">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group block border border-background-200/70 rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 mt-2 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="text-xs text-primary-500 font-medium">
                        {article.categoryName}
                      </span>
                      <span className="text-foreground-300">·</span>
                      <span className="text-xs text-foreground-500">
                        {article.date}
                      </span>
                      <span className="text-foreground-300">·</span>
                      <span className="text-xs text-foreground-500">
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-400 text-lg">
                该分类下暂无文章
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-primary-500 text-sm font-medium mt-4 hover:text-primary-600 transition-colors"
              >
                返回首页
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}